/**
 * VoucherForm — formulario de creación/edición de comprobantes contables (CON-03).
 *
 * Extraído de VouchersTab para poder reutilizarlo desde la Vista 360 (ventas y
 * compras), donde se edita el comprobante de un documento sin salir de la vista.
 *
 * Uso desde la 360: pasar `voucher={{ id } as AccountingVoucher}` (sin líneas) →
 * el formulario detecta `needsFetch` y trae el comprobante completo por su id.
 */
import { todayISO } from '../../../lib/utils'
import { useState, useEffect, useRef, useMemo } from 'react'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { fmtDate } from '@/lib/utils'
import { Plus, Loader2, Trash2, Paperclip, Eye, Upload, Image, File, RefreshCw, AlertTriangle } from 'lucide-react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import {
  useVoucher, useCreateVoucher, useUpdateVoucher,
  useAccounts, useCostCenters, useCurrencyRateForDate,
  useVoucherAttachments, useUploadVoucherAttachment, useDeleteVoucherAttachment,
  type AccountingVoucher, type VoucherLine, type VoucherType,
  type VoucherAttachment,
} from '../hooks/useAccounting'

export const TYPE_LABELS: Record<VoucherType, string> = {
  manual:     'Manual',
  sale:       'Venta',
  purchase:   'Compra',
  adjustment: 'Ajuste',
  reversal:   'Reversión',
}

const EMPTY_LINE: VoucherLine = {
  account: '', glosa: '', debe: '0', haber: '0',
}

/** Una línea con su POSICIÓN adentro — ver el comentario de la tabla. */
type FilaDeLinea = VoucherLine & { idx: number }

/** El estilo de los campos de la tabla de líneas, que son todos iguales. */
const CELDA = 'w-full border border-divider rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary'

export function fmtCLP(value: string | undefined) {
  if (!value) return '—'
  const n = parseFloat(value)
  if (isNaN(n)) return value
  return formatMoneyForTenant(n)
}

interface VoucherFormProps {
  /** Comprobante a editar. Puede ser el objeto completo (módulo Contabilidad) o
   *  un mínimo `{ id }` (Vista 360) — en ese caso se carga completo por su id. */
  voucher?: (Partial<AccountingVoucher> & { id: string }) | null
  onClose: () => void
  /** Callback opcional tras guardar con éxito (además de cerrar). La 360 lo usa
   *  para invalidar sus propias queries y refrescar la pestaña del comprobante. */
  onSaved?: () => void
}

export function VoucherForm({ voucher: voucherProp, onClose, onSaved }: VoucherFormProps) {
  const locale = useTenantLocale()
  // If we got a voucher without lines (e.g. from list item), fetch the full one
  const needsFetch = !!voucherProp && !voucherProp.lines
  const { data: fetchedVoucher, isLoading: isFetchingVoucher } = useVoucher(
    needsFetch ? voucherProp.id : ''
  )
  const voucher = needsFetch ? (fetchedVoucher ?? null) : voucherProp

  const isEdit = !!voucherProp
  const today = todayISO()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [voucherType, setVoucherType] = useState<VoucherType>(voucher?.voucher_type ?? 'manual')
  const [date, setDate] = useState(voucher?.date ?? today)
  const [description, setDescription] = useState(voucher?.description ?? '')
  const [notes, setNotes] = useState(voucher?.notes ?? '')
  const [lines, setLines] = useState<VoucherLine[]>(
    voucher?.lines?.length ? voucher.lines : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]
  )
  const [currency, setCurrency] = useState<string>(voucher?.currency || 'CLP')
  const [exchangeRate, setExchangeRate] = useState<string>(voucher?.exchange_rate ?? '')
  const [manualRate, setManualRate] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: rateData, isLoading: isLoadingRate, isError: isRateError, refetch: refetchRate } =
    useCurrencyRateForDate(currency !== 'CLP' ? currency : '', date)

  // Sync fetched rate to state (unless user is using manual override)
  useEffect(() => {
    if (rateData && !manualRate) {
      setExchangeRate(rateData.rate)
    }
  }, [rateData, manualRate])

  // Sync state when fetched voucher arrives
  useEffect(() => {
    if (fetchedVoucher && needsFetch) {
      setVoucherType(fetchedVoucher.voucher_type)
      setDate(fetchedVoucher.date)
      setDescription(fetchedVoucher.description)
      setNotes(fetchedVoucher.notes ?? '')
      setLines(fetchedVoucher.lines?.length ? fetchedVoucher.lines : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }])
      setCurrency(fetchedVoucher.currency || 'CLP')
      setExchangeRate(fetchedVoucher.exchange_rate ?? '')
    }
  }, [fetchedVoucher, needsFetch])

  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const { data: ccData } = useCostCenters({ is_active: 'true' })
  const accounts = accountsData?.accounts ?? []
  const costCenters = ccData?.cost_centers ?? []

  const create = useCreateVoucher()
  const update = useUpdateVoucher(voucher?.id ?? voucherProp?.id ?? '')
  const isPending = create.isPending || update.isPending

  // Items de autocompletado para cuentas contables
  const accountItems: AutocompleteItem[] = accounts.map(a => ({
    id: a.id,
    label: `${a.code} — ${a.name}`,
    sublabel: a.account_type,
  }))

  const totalDebe = lines.reduce((s, l) => s + parseFloat(l.debe || '0'), 0)
  const totalHaber = lines.reduce((s, l) => s + parseFloat(l.haber || '0'), 0)
  const isBalanced = Math.abs(totalDebe - totalHaber) < 0.01
  const rateValue = parseFloat(exchangeRate || '0')

  /** Las líneas con su posición adentro — ver el comentario de la tabla. */
  const filasDeLinea = useMemo<FilaDeLinea[]>(
    () => lines.map((l, idx) => ({ ...l, idx })),
    [lines],
  )

  const addLine = () => setLines(prev => [...prev, { ...EMPTY_LINE }])
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i))
  const updateLine = (i: number, field: keyof VoucherLine, value: string) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l))
  }

  const handleSave = () => {
    if (!description || !date) return
    setError(null)
    const validLines = lines.filter(l => l.account)
    const payload: Record<string, unknown> = {
      voucher_type: voucherType,
      date,
      description,
      notes,
      currency: currency !== 'CLP' ? currency : '',
      exchange_rate: currency !== 'CLP' && exchangeRate ? exchangeRate : null,
      exchange_rate_date: currency !== 'CLP' && exchangeRate ? date : null,
      lines: validLines.map((l, i) => ({ ...l, order: i + 1, cost_center: l.cost_center || null })),
    }
    const cb = {
      onSuccess: () => { onSaved?.(); onClose() },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setError(err.response?.data?.error?.message ?? 'Error al guardar')
      },
    }
    if (isEdit) update.mutate(payload, cb)
    else create.mutate(payload, cb)
  }

  if (needsFetch && isFetchingVoucher) {
    return (
      <Modal open onClose={onClose} title="Editar Comprobante" size="2xl">
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-content-subtle mr-2" />
          <span className="text-sm text-content-muted">Cargando comprobante…</span>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar Comprobante' : 'Nuevo Comprobante'} size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={isPending || !description || !date}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Guardar Cambios' : 'Crear Comprobante'}
        </button>
      </>}
    >
      <div className="overflow-y-auto flex-1 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <label htmlFor="voucherform-tipo" className="block text-xs font-medium text-content-muted mb-1">Tipo</label>
            <select id="voucherform-tipo"
              value={voucherType}
              onChange={e => setVoucherType(e.target.value as VoucherType)}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="voucherform-fecha" className="block text-xs font-medium text-content-muted mb-1">Fecha *</label>
            <DatePicker id="voucherform-fecha" value={date} onChange={setDate} />
          </div>
          <div>
            <label htmlFor="voucherform-moneda" className="block text-xs font-medium text-content-muted mb-1">Moneda</label>
            <select id="voucherform-moneda"
              value={currency}
              onChange={e => { setCurrency(e.target.value); setManualRate(false); setExchangeRate('') }}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="CLP">CLP</option>
              <option value="USD">USD</option>
              <option value="UF">UF</option>
              <option value="EUR">EUR</option>
              <option value="JPY">JPY</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="voucherform-descripcion-glosa-general" className="block text-xs font-medium text-content-muted mb-1">Descripción / Glosa General *</label>
            <input id="voucherform-descripcion-glosa-general"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="ej. Pago de remuneraciones Enero 2026"
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Panel de tasa de cambio — visible solo si moneda ≠ CLP */}
        {currency !== 'CLP' && (
          <div className="rounded-lg border border-divider bg-glass-3 px-4 py-3 text-sm">
            {isLoadingRate && (
              <div className="flex items-center gap-2 text-content-muted">
                <Loader2 size={14} className="animate-spin" />
                <span>Obteniendo tasa de cambio…</span>
              </div>
            )}
            {!isLoadingRate && !isRateError && rateData && !manualRate && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-content font-medium">
                  1 {currency} = ${parseFloat(exchangeRate || '0').toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CLP
                </span>
                <span className="text-xs text-content-subtle">({rateData.rate_date} · {rateData.source})</span>
                <button
                  type="button"
                  onClick={() => { setManualRate(false); refetchRate() }}
                  className="p-1 rounded hover:bg-surface-muted text-content-muted"
                  title="Actualizar tasa"
                >
                  <RefreshCw size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => setManualRate(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Ingresar manualmente
                </button>
              </div>
            )}
            {!isLoadingRate && (isRateError || (!rateData && !manualRate)) && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-warning-surface text-warning-content rounded border border-warning-border">
                  <AlertTriangle size={12} />
                  Sin tasa disponible para esta fecha
                </span>
                <button
                  type="button"
                  onClick={() => setManualRate(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Ingresar tasa manualmente
                </button>
              </div>
            )}
            {manualRate && (
              <div className="flex items-center gap-3 flex-wrap">
                <label htmlFor="voucherform-tasa-manual" className="text-xs text-content-muted">Tasa manual (1 {currency} = __ CLP):</label>
                <NumericInput id="voucherform-tasa-manual"
                  value={exchangeRate}
                  onChange={setExchangeRate}
                  locale={locale}
                  decimals={2}
                  className="w-36 border border-divider rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary"
                />
                {rateData && (
                  <button
                    type="button"
                    onClick={() => { setManualRate(false); setExchangeRate(rateData.rate) }}
                    className="text-xs text-content-muted hover:underline"
                  >
                    Usar tasa automática ({rateData.rate_date})
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Lines */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-content-muted">Líneas del Comprobante *</label>
            <span className={`text-xs font-medium ${isBalanced ? 'text-success-content-soft' : 'text-danger-solid'}`}>
              {currency !== 'CLP' ? (
                <>
                  Debe: {totalDebe.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                  {' | '}
                  Haber: {totalHaber.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                  {rateValue > 0 && (
                    <span className="text-content-subtle font-normal">
                      {' '}(≈ {fmtCLP(String(Math.round(totalDebe * rateValue)))} CLP)
                    </span>
                  )}
                </>
              ) : (
                <>Debe: {fmtCLP(String(totalDebe))} | Haber: {fmtCLP(String(totalHaber))}</>
              )}
              {!isBalanced && ' ⚠ No cuadra'}
            </span>
          </div>
          {/*
            Las filas llevan su POSICIÓN adentro: `updateLine` y `removeLine`
            reciben el índice del array y el `render` recibe el de la fila ya
            ordenada. Con el índice en el dato los dos coinciden siempre.
            🔴 Aun así ninguna columna lleva `sortable`: el orden de las líneas
            es del asiento — es como se lee el comprobante — no una vista de
            los datos.
          */}
          <DataTable<FilaDeLinea>
            columns={[
              { key: 'account', header: 'Cuenta *', width: '24%',
                render: f => (
                  <Autocomplete
                    items={accountItems}
                    value={f.account}
                    displayValue={
                      f.account
                        ? accounts.find(a => a.id === f.account)
                          ? `${accounts.find(a => a.id === f.account)!.code} — ${accounts.find(a => a.id === f.account)!.name}`
                          : (f.account_code ? `${f.account_code} — ${f.account_name}` : '')
                        : ''
                    }
                    onChange={val => updateLine(f.idx, 'account', val)}
                    placeholder="Buscar cuenta…"
                    aria-label={`Cuenta de la línea ${f.idx + 1}`}
                    className={CELDA}
                  />
                ) },
              { key: 'cost_center', header: 'Centro de Costo', width: '18%',
                render: f => (
                  <EntityAutocomplete
                    options={costCenters.map(c => ({ id: c.id, label: `${c.code} — ${c.name}` }))}
                    value={f.cost_center ?? ''}
                    onChange={val => updateLine(f.idx, 'cost_center', val)}
                    placeholder="—"
                    allowClear
                    aria-label={`Centro de costo de la línea ${f.idx + 1}`}
                    className={CELDA}
                  />
                ) },
              { key: 'glosa', header: 'Glosa',
                render: f => (
                  <input
                    value={f.glosa}
                    onChange={e => updateLine(f.idx, 'glosa', e.target.value)}
                    placeholder="Glosa"
                    aria-label={`Glosa de la línea ${f.idx + 1}`}
                    className={CELDA}
                  />
                ) },
              { key: 'debe', numeric: true, width: '14%',
                header: `Debe${currency !== 'CLP' ? ` (${currency})` : ''}`,
                render: f => (
                  <NumericInput
                    value={f.debe}
                    onChange={v => updateLine(f.idx, 'debe', v)}
                    locale={locale} decimals={currency !== 'CLP' ? 2 : 0}
                    aria-label={`Debe de la línea ${f.idx + 1}`}
                    className={`${CELDA} text-right`}
                  />
                ) },
              { key: 'haber', numeric: true, width: '14%',
                header: `Haber${currency !== 'CLP' ? ` (${currency})` : ''}`,
                render: f => (
                  <NumericInput
                    value={f.haber}
                    onChange={v => updateLine(f.idx, 'haber', v)}
                    locale={locale} decimals={currency !== 'CLP' ? 2 : 0}
                    aria-label={`Haber de la línea ${f.idx + 1}`}
                    className={`${CELDA} text-right`}
                  />
                ) },
              { key: 'quitar', header: '', width: 36,
                render: f => (
                  <div className="text-center">
                    {lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(f.idx)}
                        aria-label={`Eliminar la línea ${f.idx + 1}`}
                        className="p-1 rounded text-content-subtle hover:text-danger-solid hover:bg-danger-surface">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ) },
            ]}
            rows={filasDeLinea}
            rowKey={f => f.idx}
            caption="Líneas del comprobante contable"
          />
          <button type="button" onClick={addLine}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover">
            <Plus size={12} />Agregar línea
          </button>
        </div>

        <div>
          <label htmlFor="voucherform-notas-internas" className="block text-xs font-medium text-content-muted mb-1">Notas internas</label>
          <textarea id="voucherform-notas-internas"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        {/* Adjuntos — solo al editar (el comprobante ya existe) */}
        {isEdit && voucher && (
          <AttachmentsSection voucherId={voucher.id} />
        )}

        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>

    </Modal>
  )
}

// ─── Attachments Section ──────────────────────────────────────────────────────

function fmtFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileIcon({ contentType }: { contentType: string }) {
  if (contentType === 'application/pdf') return <File size={14} className="text-danger-solid" />
  if (contentType.startsWith('image/')) return <Image size={14} className="text-primary" />
  return <Paperclip size={14} className="text-content-subtle" />
}

export function AttachmentsSection({ voucherId }: { voucherId: string }) {
  const locale = useTenantLocale()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, isLoading } = useVoucherAttachments(voucherId)
  const upload = useUploadVoucherAttachment()
  const deleteAttachment = useDeleteVoucherAttachment()
  const [error, setError] = useState<string | null>(null)
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)
    upload.mutate({ voucherId, file }, {
      onError: (err: unknown) => {
        const apiErr = err as { response?: { data?: { error?: { message?: string } } } }
        setError(apiErr.response?.data?.error?.message ?? 'Error al subir archivo')
      },
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDelete = (attachmentId: string, fileName: string) => {
    confirmAction({
      message: `¿Eliminar "${fileName}"?`,
      variant: 'danger',
      onConfirm: () => deleteAttachment.mutate({ voucherId, attachmentId }),
    })
  }

  const totalSize = data?.total_size ?? 0
  const maxSize = data?.max_size ?? 20 * 1024 * 1024
  const usagePercent = Math.min(100, (totalSize / maxSize) * 100)

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-content-muted uppercase flex items-center gap-1.5">
          <Paperclip size={12} />
          Adjuntos ({data?.count ?? 0})
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-content-subtle">
            {fmtFileSize(totalSize)} / {fmtFileSize(maxSize)}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50"
          >
            {upload.isPending ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
            Subir archivo
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {(data?.count ?? 0) > 0 && (
        <div className="w-full h-1.5 bg-surface-muted rounded-full mb-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${usagePercent > 80 ? 'bg-warning-solid' : 'bg-primary/60'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      )}

      {error && (
        <div className="mb-2 text-xs text-danger-content-soft bg-danger-surface border border-danger-border rounded px-2 py-1">{error}</div>
      )}

      {isLoading ? (
        <div className="text-xs text-content-subtle py-2">Cargando adjuntos...</div>
      ) : !data?.attachments.length ? (
        <div className="text-xs text-content-subtle py-2 text-center">Sin adjuntos</div>
      ) : (
        <div className="space-y-1">
          {data.attachments.map((att: VoucherAttachment) => (
            <div key={att.id}
              className="flex items-center justify-between px-2 py-1.5 bg-glass-3 rounded-lg border border-divider group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon contentType={att.content_type} />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-content truncate max-w-[200px]">{att.file_name}</p>
                  <p className="text-[10px] text-content-subtle">
                    {fmtFileSize(att.file_size)}
                    {att.uploaded_by_name && ` · ${att.uploaded_by_name}`}
                    {` · ${fmtDate(att.created_at, locale)}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={att.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 rounded text-content-subtle hover:text-primary hover:bg-primary-50"
                  title="Ver"
                >
                  <Eye size={12} />
                </a>
                <button
                  onClick={() => handleDelete(att.id, att.file_name)}
                  disabled={deleteAttachment.isPending}
                  className="p-1 rounded text-content-subtle hover:text-danger-solid hover:bg-danger-surface opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Eliminar"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}
